import React, { useEffect, useState } from 'react'
import { Skeleton, Stack, Typography, useTheme, styled, Divider } from '@mui/material'
import {
  useActiveOrganization,
  useFetchActiveOrganization,
  useFetchNotificationsListSubscriptions,
} from 'hooks/use-organization'
import { NotificationType, OrganizationFull } from 'api/model'
import { notificationsUpdateSubscriptions } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useSetRecoilState } from 'recoil'
import { toastAtom } from 'state'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import { CheckboxWithDarkMode } from 'dark-mode-components'
import { getColorForDarkMode } from 'utils'
import MyProfile from 'components/UserProfile/MyProfile'
import { COLORS } from 'app-constants'
import DeleteAccount from './DeleteAccount'

const ExtLink = styled('a')`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  letter-spacing: 0;
  color: #666666;
  text-align: left;
  padding-left: 0px;
`

const ChecklistRowStack = styled(Stack)`
  flex-direction: row;
  align-items: center;
  row-spacing: 4px;
`

const Account = () => {
  const isMobile = useCheckMobileScreen()
  const { organization, error } = useFetchActiveOrganization()

  const renderContent = () => {
    if (error) {
      return <div>{error}</div>
    }

    const renderForm = () => {
      if (!organization) {
        return <LoadingAccount />
      }

      return <EditOrganization organization={organization} />
    }

    return <Stack direction="column">{renderForm()}</Stack>
  }

  return (
    <Stack
      sx={{
        p: 3,
        borderLeft: '1px solid #e0e0e0',
        backgroundColor: COLORS.GRAY_8,
      }}>
      {!isMobile && (
        <Typography variant="h4" mb={3}>
          Account
        </Typography>
      )}

      {renderContent()}
    </Stack>
  )
}

const EditOrganization = ({ organization }: { organization: OrganizationFull }) => {
  const [activeOrganization] = useActiveOrganization()
  const theme = useTheme()
  const isMobile = useCheckMobileScreen()

  const setToast = useSetRecoilState(toastAtom)

  const [industry, setIndustry] = useState<string>('')
  useEffect(() => {
    if (!industry && organization?.industry) {
      setIndustry(organization?.industry)
      setHasChanges(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization.industry])

  useEffect(() => {
    if (!hasChanges) {
      setHasChanges(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry])

  const [hasChanges, setHasChanges] = useState(false)

  const { notificationSubscriptions } = useFetchNotificationsListSubscriptions()

  const [isWeeklyInsightsChecked, setIsWeeklyInsightsChecked] = useState(false)
  const onCheckWeeklyInsights = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsWeeklyInsightsChecked(event.target.checked)

    onUpdateSubscription(event.target.checked, NotificationType.weekly_responder_insights)
  }

  const [isTrendUpdatesChecked, setIsTrendUpdatesChecked] = useState(false)
  const onCheckTrendUpdates = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsTrendUpdatesChecked(event.target.checked)

    onUpdateSubscription(event.target.checked, NotificationType.trend_updates)
  }

  const onUpdateSubscription = async (checked: boolean, notificationType: NotificationType) => {
    if (!activeOrganization) {
      return
    }

    try {
      await notificationsUpdateSubscriptions(activeOrganization, [
        {
          type: notificationType,
          is_enabled: checked,
        },
      ])
      // set success toast
      setToast({ title: 'Successfully updated email notifications', severity: 'success' })
    } catch (error) {
      setToast({ title: 'Failed to update email notification failed', severity: 'error' })
    }
  }

  // todo: need to populate the default state for the checkmarks based on what comes back from the fetch
  useEffect(() => {
    const weeklyInsightsSubscription = notificationSubscriptions.find(
      (subscription) => subscription.type === NotificationType.weekly_responder_insights
    )
    if (weeklyInsightsSubscription) {
      setIsWeeklyInsightsChecked(weeklyInsightsSubscription.is_enabled)
    }

    const trendUpdatesSubscription = notificationSubscriptions.find(
      (subscription) => subscription.type === NotificationType.trend_updates
    )
    if (trendUpdatesSubscription) {
      setIsTrendUpdatesChecked(trendUpdatesSubscription.is_enabled)
    }
  }, [notificationSubscriptions])

  return (
    <Stack>
      <Stack direction={isMobile ? 'column' : 'row'} alignItems="flex-start" spacing={isMobile ? 3 : 12}>
        <Typography variant="h5" width="120px">
          Profile
        </Typography>

        <MyProfile />
      </Stack>

      <Divider
        sx={{
          my: 4,
        }}
      />

      <Stack direction="row" alignItems="flex-start" spacing={9.3}>
        <Typography variant="h5">Email preferences</Typography>
        <Stack spacing={2}>
          <ChecklistRowStack>
            <CheckboxWithDarkMode
              disableRipple
              theme={theme}
              checked={isWeeklyInsightsChecked}
              onChange={onCheckWeeklyInsights}
              color="primary"
            />
            <Typography variant="pS">Weekly insights</Typography>
          </ChecklistRowStack>
          <ChecklistRowStack>
            <CheckboxWithDarkMode
              disableRipple
              theme={theme}
              checked={isTrendUpdatesChecked}
              onChange={onCheckTrendUpdates}
              color="primary"
            />
            <Typography variant="pS">Trend updates (maximum of once a day)</Typography>
          </ChecklistRowStack>
        </Stack>
      </Stack>

      <Divider
        sx={{
          my: 4,
        }}
      />

      <DeleteAccount />

      <Divider
        sx={{
          my: 4,
        }}
      />

      <Stack spacing={2} mt={2} mb={isMobile ? 4 : 0}>
        <ExtLink
          href="https://www.jelled.ai/privacy-policy"
          target="_blank"
          rel="noreferrer"
          sx={{
            color: getColorForDarkMode(theme),
          }}>
          Privacy policy
        </ExtLink>
        <ExtLink
          href="https://www.jelled.ai/terms-of-service"
          target="_blank"
          rel="noreferrer"
          sx={{
            color: getColorForDarkMode(theme),
          }}>
          Terms of service
        </ExtLink>
      </Stack>
    </Stack>
  )
}

const LoadingAccount = () => {
  const isMobile = useCheckMobileScreen()

  return (
    <Stack spacing={4} sx={{ width: isMobile ? '100%' : '376px' }}>
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={104} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
      <Skeleton variant="rectangular" height={56} />
    </Stack>
  )
}

export default Account
